import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileAudio, FolderOpen, Mic2, Music4, Scissors, SlidersHorizontal, Upload, UserRound, Users, Wand2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import { getCurrentSongId, proStudioPath } from "@/lib/studioMode";

const MAIN = [
  { key: "record", icon: Mic2, title: "My Voice Studio", body: "Guided mic setup, then record yourself singing over any music.", to: "/create/studio", accent: "from-rose-500 to-red-500" },
  { key: "song", icon: Music4, title: "Generate Song", body: "Your lyrics, gospel reggae, R&B and more — in your own voice.", to: "/create/song", accent: "from-blue-500 to-violet-600" },
  { key: "enhance", icon: Wand2, title: "Enhance Vocals", body: "Upload a vocal and compare Original, Natural Voice and Studio-Polished.", to: "/create/enhance", accent: "from-amber-300 to-orange-500" },
  { key: "bgv", icon: Users, title: "Background Singers", body: "Harmonies and a gospel choir behind your lead — male, female or both.", to: "/create/background-singers", accent: "from-violet-400 to-fuchsia-500" },
  { key: "my-voices", icon: UserRound, title: "My Voices", body: "Teach ELPM your real singing voice, then pick it when you make a song.", to: "/create/my-voices", accent: "from-cyan-300 to-emerald-400" },
  { key: "export-audio", icon: FileAudio, title: "Export Audio", body: "Full mix and stems as 24-bit 48 kHz WAV + MP3, ready for Studio One.", to: "/create/export-audio", accent: "from-sky-400 to-indigo-500" },
];

const MORE = [
  { key: "upload", icon: Upload, title: "Upload a Song", body: "Import an existing song or beat.", intent: "upload", accent: "from-amber-400 to-orange-500" },
  { key: "stems", icon: Scissors, title: "Separate Stems", body: "Vocals, drums, bass, piano and more.", intent: "stems", accent: "from-cyan-400 to-sky-500" },
  { key: "master", icon: SlidersHorizontal, title: "Mix & Master", body: "Make a song sound professional.", to: "/create/master", accent: "from-emerald-400 to-teal-500" },
  { key: "projects", icon: FolderOpen, title: "My Music Library", body: "Projects, versions, recordings and masters.", to: "/create/projects", accent: "from-fuchsia-500 to-pink-500" },
];

const CARD_INIT = { opacity: 0, y: 22 };
const CARD_IN = { opacity: 1, y: 0 };

function Card({ c, i, songId, small }) {
  const navigate = useNavigate();
  const Icon = c.icon;
  return (
    <motion.button type="button" initial={CARD_INIT} animate={CARD_IN} transition={{ delay: i * 0.05, duration: 0.4 }} data-testid={`hub-choice-${c.key}`}
      onClick={() => navigate(c.to || proStudioPath(songId, c.intent))}
      className={`group relative overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 text-left transition-transform duration-300 hover:-translate-y-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${small ? "flex items-center gap-4 p-5" : "p-7"}`}>
      <div className={`pointer-events-none absolute -right-14 -top-14 h-44 w-44 rounded-full bg-gradient-to-br ${c.accent} opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-40`} />
      <div className={`grid flex-shrink-0 place-items-center rounded-2xl bg-gradient-to-br ${c.accent} text-white shadow-lg ${small ? "h-11 w-11" : "mb-6 h-14 w-14"}`}>
        <Icon className={small ? "h-5 w-5" : "h-6 w-6"} />
      </div>
      <div className="min-w-0">
        <h3 className={`font-display font-semibold ${small ? "text-base" : "text-xl"}`}>{c.title}</h3>
        <p className={`leading-relaxed text-white/60 ${small ? "mt-0.5 text-[12px]" : "mt-2 text-sm"}`}>{c.body}</p>
        {small ? null : (
          <span className="mt-6 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45 transition-colors group-hover:text-white">
            Start <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
          </span>
        )}
      </div>
    </motion.button>
  );
}

/** "What would you like to create today?" — the six studio buttons, then more tools. */
export default function CreatorHub() {
  const songId = getCurrentSongId();
  return (
    <CreatorShell testId="creator-hub" back="/" title="Welcome to ELPM Studio" subtitle="What would you like to create today?">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="hub-choices">
        {MAIN.map((c, i) => <Card key={c.key} c={c} i={i} songId={songId} />)}
      </div>
      <h2 className="mb-3 mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">More tools</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="hub-more">
        {MORE.map((c, i) => <Card key={c.key} c={c} i={i + MAIN.length} songId={songId} small />)}
      </div>
      <div className="mt-10 flex justify-center">
        <Link to={proStudioPath(songId)} data-testid="hub-advanced-studio" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.18em] text-white/50 transition-colors hover:text-white">
          Advanced Studio <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </CreatorShell>
  );
}
